import { Fragment } from 'react';
import type { DocBlock, DocPageContent } from '../content/types';
import { hubspotApp } from '../content/hubspotApp';
import { security } from '../content/security';
import { support } from '../content/support';
import s from '../site.module.css';
import { Section, SiteLayout } from './layout';

function Block({ block }: { block: DocBlock }) {
  switch (block.kind) {
    case 'p':
      return <p className={s.docText}>{block.text}</p>;
    case 'h3':
      return <h3 className={s.docH3}>{block.text}</h3>;
    case 'ul':
      return (
        <ul className={s.plainList}>
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol className={s.docSteps}>
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ol>
      );
    case 'links':
      return (
        <ul className={s.docLinks}>
          {block.items.map((l) => (
            <li key={l.href}>
              <a href={l.href}>{l.label}</a>
            </li>
          ))}
        </ul>
      );
    case 'table':
      return (
        <div className={s.docTableWrap}>
          <table className={`${s.verdictTable} ${s.docTable}`}>
            <caption className="visually-hidden">{block.caption}</caption>
            <thead>
              <tr>
                {block.columns.map((c) => (
                  <th key={c} scope="col">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row) => (
                <tr key={row[0]}>
                  {row.map((cell, i) =>
                    i === 0 ? (
                      <th key={i} scope="row">
                        {cell}
                      </th>
                    ) : (
                      <td key={i}>{cell}</td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

/** A reference page at its own address: a hero, then one section per heading. Used by the HubSpot app, support and security pages. */
function DocPage({ page }: { page: DocPageContent }) {
  return (
    <SiteLayout>
      <section className={s.hero} aria-labelledby="hero-title">
        <div className={s.container}>
          <div className={s.heroCopy}>
            <p className={s.eyebrow}>{page.hero.eyebrow}</p>
            <h1 id="hero-title">{page.hero.headline}</h1>
            <p className={s.lede}>{page.hero.subhead}</p>
          </div>
        </div>
      </section>
      {page.sections.map((section, i) => (
        <Section key={section.id} id={section.id} tone={i % 2 === 0 ? 'white' : 'mist'} labelledBy={`${section.id}-title`}>
          <div className={s.docBody}>
            <h2 id={`${section.id}-title`}>{section.headline}</h2>
            {section.blocks.map((block, j) => (
              <Fragment key={j}>
                <Block block={block} />
              </Fragment>
            ))}
          </div>
        </Section>
      ))}
    </SiteLayout>
  );
}

export const HubspotAppPage = () => <DocPage page={hubspotApp} />;
export const SupportPage = () => <DocPage page={support} />;
export const SecurityPage = () => <DocPage page={security} />;
